import { CircularProgress } from "@mui/material";
import { withSentryReactRouterV7Routing } from "@sentry/react";
import { enqueueSnackbar } from "notistack";
import { FC, PropsWithChildren, useContext, useEffect } from "react";
import { Navigate, Outlet, Params, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { Permissions } from "./api/dtos";
import DeleteDialog, { DeleteTargetTypes } from "./components/dialogs/DeleteDialog";
import DefaultLayout from "./components/layout/DefaultLayout";
import PermissionsProvider from "./components/providers/auth/PermissionsProvider";
import LinkProvider from "./components/providers/layout/LinkProvider";
import LotProvider from "./components/providers/LotProvider";
import LotsProvider from "./components/providers/LotsProvider";
import ProjectProvider from "./components/providers/ProjectProvider";
import ProjectsProvider from "./components/providers/ProjectsProvider";
import StagePricelistProvider from "./components/providers/StagePricelistProvider";
import StageProvider from "./components/providers/StageProvider";
import StagesProvider from "./components/providers/StagesProvider";
import RedirectWithContext from "./components/RedirectWithContext";
import AgentFormByIdRoute from "./components/routes/AgentFormByIdRoute";
import AgentLocationFormByIdRoute from "./components/routes/AgentLocationFormByIdRoute";
import AssetFormByIdRoute from "./components/routes/AssetFormByIdRoute";
import AttachmentFormByIdRoute from "./components/routes/AttachmentFormByIdRoute";
import AttachmentTypeFormByIdRoute from "./components/routes/AttachmentTypeFormByIdRoute";
import ContractFormByIdRoute from "./components/routes/ContractFormByIdRoute";
import LotFormByIdRoute from "./components/routes/LotFormByIdRoute";
import LotNoteFormByIdRoute from "./components/routes/LotNoteFormByIdRoute";
import LotPropertyFormByIdRoute from "./components/routes/LotPropertyFormByIdRoute";
import PrecinctFormByIdRoute from "./components/routes/PrecinctFormByIdRoute";
import ProjectAgentLocationFormByIdRoute from "./components/routes/ProjectAgentLocationFormByIdRoute";
import ProjectFormByIdRoute from "./components/routes/ProjectFormByIdRoute";
import PropertyFormByIdRoute from "./components/routes/PropertyFormByIdRoute";
import StageFormByIdRoute from "./components/routes/StageFormByIdRoute";
import TenantFormByIdRoute from "./components/routes/TenantFormByIdRoute";
import UserAgentLocationFormByIdRoute from "./components/routes/UserAgentLocationFormByIdRoute";
import UserProjectFormByIdRoute from "./components/routes/UserProjectFormByIdRoute";
import PermissionsContext from "./contexts/auth/PermissionsContext";
import AgentForm from "./forms/admin/AgentForm";
import AgentLocationForm from "./forms/admin/AgentLocationForm";
import AgentPersonnelForm from "./forms/admin/AgentPersonnelForm";
import AgentProjectForm from "./forms/admin/AgentProjectForm";
import AttachmentTypeForm from "./forms/admin/AttachmentTypeForm";
import PrecinctForm from "./forms/admin/PrecinctForm";
import PropertyForm from "./forms/admin/PropertyForm";
import TenantForm from "./forms/admin/TenantForm";
import UserProjectForm from "./forms/admin/UserProjectForm";
import AssetForm from "./forms/AssetForm";
import AttachmentForm from "./forms/AttachmentForm";
import ContractForm from "./forms/ContractForm";
import LotForm from "./forms/LotForm";
import LotNoteForm from "./forms/LotNoteForm";
import LotPropertyForm from "./forms/LotPropertyForm";
import ProjectForm from "./forms/ProjectForm";
import StageForm from "./forms/StageForm";
import StagePricelistDownloadForm from "./forms/StagePricelistDownloadForm";
import StagePricelistForm from "./forms/StagePricelistForm";
import usePricelistDownloadByStageIdSubstage from "./hooks/data/usePricelistDownloadByStageIdSubstage";
import usePricelistSubstagesByStageId from "./hooks/data/usePricelistSubstagesByStageId";
import useStagePricelistByStageIdSubStage from "./hooks/data/useStagePricelistByStageIdSubStage";
import useSentryUser from "./hooks/sentry/useSentryUser";
import Administration from "./pages/admin/Administration";
import AgentLocations from "./pages/admin/AgentLocations";
import AgentPersonnel from "./pages/admin/AgentPersonnel";
import AgentProjects from "./pages/admin/AgentProjects";
import Agents from "./pages/admin/Agents";
import AttachmentTypes from "./pages/admin/AttachmentTypes";
import LotProperties from "./pages/admin/LotProperties";
import ProjectPermissions from "./pages/admin/ProjectPermissions";
import StagePrecincts from "./pages/admin/StagePrecincts";
import Tenants from "./pages/admin/Tenants";
import { BulkEditContracts } from "./pages/BulkEditContracts";
import { BulkEditLotProperties } from "./pages/BulkEditLotProperties";
import { BulkEditLots } from "./pages/BulkEditLots";
import { BulkEditProjectCommissions } from "./pages/BulkEditProjectCommissions";
import { BulkEditProjectPrecincts } from "./pages/BulkEditProjectPrecincts";
import DefaultError from "./pages/error/DefaultError";
import Finances from "./pages/finances/Finances";
import { LotDetails } from "./pages/LotDetails";
import { ProjectDetails } from "./pages/ProjectDetails";
import { Projects } from "./pages/Projects";
import ReportCustomTemplate from "./pages/reports/ReportCustomTemplate";
import ReportDetails from "./pages/reports/ReportDetails";
import ReportPreview from "./pages/reports/ReportPreview";
import Reports from "./pages/reports/Reports";
import ReportTemplatePreview from "./pages/reports/ReportTemplatePreview";
import ReportTemplates from "./pages/reports/ReportTemplates";
import { StageDetails } from "./pages/StageDetails";
import StagePlanEdit from "./pages/stageplan/StagePlanEdit";
import StagePlanView from "./pages/stageplan/StagePlanView";
import StagePlanViewPublic from "./pages/stageplan/StagePlanViewPublic";

interface IFormRouteProps {
    edit?: boolean;
    createRoute: (params: Readonly<Params<string>>) => string;
}

interface IDeleteDialogRouteProps {
    targetType: DeleteTargetTypes;
    createRoute: (params: Readonly<Params<string>>) => string;
}

const DeleteDialogRoute: FC<IDeleteDialogRouteProps> = ({
    targetType,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    let targetId = -1;

    switch(targetType) {
        case DeleteTargetTypes.Stage:
            targetId = Number(params.stageId);
            break;
        case DeleteTargetTypes.Lot:
            targetId = Number(params.lotId);
            break;
        case DeleteTargetTypes.Contract:
            targetId = Number(params.contractId);
            break;
        case DeleteTargetTypes.Attachment:
            targetId = Number(params.attachmentId);
            break;
        case DeleteTargetTypes.LotNote:
            targetId = Number(params.lotNoteId);
            break;
        case DeleteTargetTypes.Report:
            targetId = Number(params.reportId);
            break;
        case DeleteTargetTypes.ReportTemplate:
            targetId = Number(params.templateId);
            break;
        case DeleteTargetTypes.Asset:
            targetId = Number(params.assetId);
            break;
        default: 
            navigate(createRoute(params));
    }

    return (
            <DeleteDialog
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        );
                    }
                }} 
                targetType={targetType} 
                targetId={targetId} 
                open={false}
            />
    )
}

const AssetFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <AssetFormByIdRoute>
            <AssetForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </AssetFormByIdRoute>
    )
}

const AttachmentFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <AttachmentFormByIdRoute>
            <AttachmentForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </AttachmentFormByIdRoute>
    )
}

const LotPropertyFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <LotPropertyFormByIdRoute>
            <LotPropertyForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </LotPropertyFormByIdRoute>
    )
}

const LotNoteFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <LotNoteFormByIdRoute>
            <LotNoteForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </LotNoteFormByIdRoute>
    )
}

const StagePricelistDownloadRoute: FC<IFormRouteProps> = ({
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const stageId = Number(params.stageId);
    const subStage = params.subStage ? +params.subStage : undefined;

    const download = usePricelistDownloadByStageIdSubstage();
    const {data: options, loading: substagesLoading} = usePricelistSubstagesByStageId(Number(params.stageId));
    const {data: pricelist, loading: pricelistLoading} = useStagePricelistByStageIdSubStage(Number(params.stageId), params.subStage ? +params.subStage : undefined);

    const handleDownload = async () => {
        try {
            const blob = await download(stageId, subStage);
            const url = window.URL.createObjectURL(blob);
    
            const link = document.createElement("a");
            link.href = url;
            link.download = `${pricelist?.name}.pdf`;
            link.click();
    
            window.URL.revokeObjectURL(url);
            navigate(createRoute(params), {replace: true});
        }
        catch(e) {
            navigate(createRoute(params), {replace: true});
            enqueueSnackbar("Failed to download pricelist", {variant: "error", preventDuplicate: true, autoHideDuration: 3000});
        }

    }

    useEffect(() => {
        if(!pricelistLoading && !substagesLoading && options && options.length == 0) {
            handleDownload();
        } 
    }, [substagesLoading, pricelistLoading, options])
    
    return (
        <>
            {
                (substagesLoading || pricelistLoading || !options || options.length == 0) ?
                <CircularProgress /> :
                <StagePricelistProvider
                    stageId={Number(params.stageId)}
                    subStage={params.subStage ? +params.subStage : undefined}
                >
                    <StagePricelistDownloadForm
                        open={true}
                        onClose={() => {
                            navigate(
                                createRoute(params),
                                { replace: true }
                            )
                        }}
                    />
                </StagePricelistProvider>
            }
        </>
    )
}

const StagePricelistFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <StagePricelistProvider
            stageId={Number(params.stageId)}
            subStage={params.subStage ? +params.subStage : undefined}
        >
            <StagePricelistForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </StagePricelistProvider>
    )
}

const UserProjectFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <UserProjectFormByIdRoute>
            <UserProjectForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </UserProjectFormByIdRoute>
    )
}

const PrecinctFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <PrecinctFormByIdRoute>
            <PrecinctForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </PrecinctFormByIdRoute>
    )
}

const AttachmentTypeFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <AttachmentTypeFormByIdRoute>
            <AttachmentTypeForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </AttachmentTypeFormByIdRoute>
    )
}

const PropertyFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <PropertyFormByIdRoute>
            <PropertyForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </PropertyFormByIdRoute>
    )
}

const TenantFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <TenantFormByIdRoute>
            <TenantForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </TenantFormByIdRoute>
    )
}

const AgentProjectFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <ProjectAgentLocationFormByIdRoute>
            <AgentProjectForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </ProjectAgentLocationFormByIdRoute>
    )
}

const AgentPersonnelFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <UserAgentLocationFormByIdRoute>
            <AgentPersonnelForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </UserAgentLocationFormByIdRoute>
    )
}

const AgentLocationFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <AgentLocationFormByIdRoute>
            <AgentLocationForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </AgentLocationFormByIdRoute>
    )
}

const AgentFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <AgentFormByIdRoute>
            <AgentForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </AgentFormByIdRoute>
    )
}

const ContractFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <ContractFormByIdRoute>
            <ContractForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </ContractFormByIdRoute>
    )
}

const LotFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <LotFormByIdRoute>
            <LotForm
                //maxWidth="sm"
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </LotFormByIdRoute>
    )
}

const StageFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <StageFormByIdRoute>
            <StageForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </StageFormByIdRoute>
    )
}

const ProjectFormRoute: FC<IFormRouteProps> = ({
    edit,
    createRoute
}) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    return (
        <ProjectFormByIdRoute
            loadBeforeMount={true}
            validateBeforeMount={{
                form: true
            }}
        >
            <ProjectForm
                open={true}
                edit={edit}
                onClose={() => {
                    if (location.state?.back) {
                        navigate(-1);
                    }
                    else {
                        navigate(
                            createRoute(params),
                            { replace: true }
                        )
                    }
                }}
            />
        </ProjectFormByIdRoute>
    )
}

const SentryRoutes = withSentryReactRouterV7Routing(Routes);

const RoutingModel: FC = () => {
    const {data: permissions, loading: permissionsLoading} = useContext(PermissionsContext);

    if(permissionsLoading) {
        return <CircularProgress />
    }

    return (
        <SentryRoutes>
            <Route
                path="/stageplan.aspx"
                element={
                    <StagePlanViewPublic />
                }
            />
            <Route 
                path="/"
                element={
                    <AppProviders>
                        <DefaultLayout />
                    </AppProviders>
                }
            >
                <Route 
                    index
                    element={
                        <Navigate to={"/projects"} />
                    }
                />
                <Route
                    path="error"
                    element={
                        <DefaultError />
                    }
                />
                <Route 
                    path="projects"
                    element={
                        permissions?.includes(Permissions.ProjectsCanViewPage)
                            ? <Projects />
                            : <RedirectWithContext variant={'project'} />
                    }
                >
                    <Route 
                        path="create"
                        element={
                            <ProjectFormRoute
                                createRoute={() => "/projects"}
                            />
                        }
                    />
                    <Route 
                        path="edit/:projectId"
                        element={
                            <ProjectFormRoute
                                edit
                                createRoute={() => "/projects"}
                            />
                        }
                    />
                    
                </Route>
                <Route
                    path="projects/:projectId"
                    element={
                        <ProjectDetails />
                    }
                >
                    <Route
                        path="edit"
                        element={
                            <ProjectFormRoute
                                edit
                                createRoute={(params) => `/projects/${params.projectId}`}
                            />
                        }
                    />
                    <Route
                        path="stages"
                    >
                        <Route
                            path="create"
                            element={
                                <StageFormRoute 
                                    createRoute={(params) => `/projects/${params.projectId}`}
                                />
                            }
                        />
                        <Route
                            path="edit/:stageId"
                            element={
                                <StageFormRoute 
                                    createRoute={(params) => `/projects/${params.projectId}`}
                                />
                            }
                        />
                        {
                            permissions?.includes(Permissions.ProjectCanDeleteStage) &&
                            <Route
                                path="delete/:stageId"
                                element={
                                    <DeleteDialogRoute 
                                        targetType={DeleteTargetTypes.Stage} 
                                        createRoute={(params) => `/projects/${params.projectId}`}
                                    />
                                }
                            />
                        }
                    </Route>
                </Route>
                {
                    permissions?.includes(Permissions.ProjectCanEditPrecincts) &&
                    <Route
                        path="projects/:projectId/project-precincts/edit"
                        element={
                            <BulkEditProjectPrecincts />
                        }
                    />
                }
                {
                    permissions?.includes(Permissions.ProjectsCanEditProject) &&
                    <>
                        <Route
                            path="projects/:projectId/project-commissions/edit"
                            element={
                                <BulkEditProjectCommissions />
                            }
                        />
                        <Route
                            path="projects/:projectId/bulk-edit-lot-properties"
                            element = {
                                <BulkEditLotProperties />
                            }
                        />
                    </>
                }
                <Route
                    path="projects/:projectId/stages/:stageId"
                    element={
                        <StageDetails />
                    }
                >
                    <Route
                        path="edit"
                        element={
                            <StageFormRoute
                                edit
                                createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                            />
                        }
                    />
                    <Route
                        path="attachments"
                        element={
                            <Outlet />
                        }    
                    >
                        <Route
                            path="create"
                            element={
                                <AttachmentFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        <Route
                            path="edit/:attachmentId"
                            element={
                                <AttachmentFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        {
                            permissions?.includes(Permissions.StageCanDeleteAttachment) &&
                            <Route
                                path="delete/:attachmentId"
                                element={
                                    <DeleteDialogRoute 
                                        targetType={DeleteTargetTypes.Attachment} 
                                        createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                    />
                                }
                            />
                        }
                    </Route>
                    <Route
                        path="download-pricelist"
                    >
                        <Route 
                            index
                            element={
                                <StagePricelistDownloadRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                />
                            }
                        />
                        <Route 
                            path=":subStage"
                            element={
                                <StagePricelistDownloadRoute
                                    edit
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                />
                            }
                        />
                    </Route>
                    <Route
                        path="style-pricelist"
                    >
                        <Route 
                            index
                            element={
                                <StagePricelistFormRoute
                                    edit
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                />
                            }
                        />
                        <Route 
                            path=":subStage"
                            element={
                                <StagePricelistFormRoute
                                    edit
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                />
                            }
                        />
                    </Route>
                    <Route
                        path="lots"
                        element={
                            <Outlet />
                        }
                    >
                        <Route 
                            path="create"
                            element={
                                <LotFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                />
                            }
                        />
                        <Route 
                            path="edit/:lotId"
                            element={
                                <LotFormRoute
                                    edit
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                />
                            }
                        />
                        {
                            permissions?.includes(Permissions.StageCanDeleteLot) &&
                            <Route
                                path="delete/:lotId"
                                element={
                                    <DeleteDialogRoute 
                                        targetType={DeleteTargetTypes.Lot} 
                                        createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}`}
                                    />
                                }
                            />
                        }
                    </Route>
                </Route>
                <Route
                    path="projects/:projectId/stages/:stageId/lots/:lotId"
                    element={
                        <LotDetails />
                    }
                >
                    <Route 
                        path="edit"
                        element={
                            <LotFormRoute
                                edit
                                createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                            />
                        }
                    />
                    <Route
                        path="attachments"
                        element={
                            <Outlet />
                        }
                    >
                        <Route
                            path="create"
                            element={
                                <AttachmentFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        <Route
                            path="edit/:attachmentId"
                            element={
                                <AttachmentFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        
                        {
                            permissions?.includes(Permissions.LotCanDeleteAttachment) &&
                            <Route
                                path="delete/:attachmentId"
                                element={
                                    <DeleteDialogRoute 
                                        targetType={DeleteTargetTypes.Attachment} 
                                        createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                    />
                                }
                            />
                        }
                    </Route>
                    <Route
                        path="notes"
                        element={
                            <Outlet />
                        }
                    >
                        <Route
                            path="create"
                            element={
                                <LotNoteFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        <Route
                            path="edit/:lotNoteId"
                            element={
                                <LotNoteFormRoute
                                    edit
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        
                        {
                            permissions?.includes(Permissions.LotCanDeleteNote) &&
                            <Route
                                path="delete/:lotNoteId"
                                element={
                                    <DeleteDialogRoute 
                                        targetType={DeleteTargetTypes.LotNote} 
                                        createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                    />
                                }
                            />
                        }
                    </Route>
                    <Route
                        path="properties"
                        element={
                            <Outlet />
                        }
                    >
                        <Route
                            path="create"
                            element={
                                <LotPropertyFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        <Route
                            path="edit/:lotPropertyId"
                            element={
                                <LotPropertyFormRoute
                                    edit
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                    </Route>
                    <Route
                        path="contracts"
                        element={<Outlet />}
                    >
                        <Route 
                            path="create"
                            element={
                                <ContractFormRoute
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        <Route 
                            path="edit/:contractId"
                            element={
                                <ContractFormRoute
                                    edit
                                    createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                                />
                            }
                        />
                        {
                            // permissions?.includes(Permissions.LotCanDeleteContract) &&
                            // <Route
                            //     path="delete/:contractId"
                            //     element={
                            //         <DeleteDialogRoute 
                            //             targetType={DeleteTargetTypes.Contract} 
                            //             createRoute={(params) => `/projects/${params.projectId}/stages/${params.stageId}/lots/${params.lotId}`}
                            //         />
                            //     }
                            // />
                        }
                    </Route>
                </Route>
                <Route
                    path="projects/:projectId/stages/:stageId/bulk-edit-lots"
                    element = {
                        <BulkEditLots />
                    }
                />
                <Route
                    path="projects/:projectId/stages/:stageId/bulk-edit-lot-properties"
                    element = {
                        <BulkEditLotProperties />
                    }
                />
                <Route
                    path="projects/:projectId/stages/:stageId/bulk-edit-contracts"
                    element = {
                        <BulkEditContracts />
                    }
                />
                <Route
                    path="projects/:projectId/stages/:stageId/stage-plan"
                    element={
                        <StagePlanView />
                    }
                />
                <Route
                    path="projects/:projectId/stages/:stageId/stage-plan/edit"
                    element={
                        <StagePlanEdit />
                    }
                />
                <Route
                    path="reports"
                    element={
                        <Reports />
                    }
                >
                    <Route
                        path="delete/:reportId"
                        element={
                            <DeleteDialogRoute 
                                targetType={DeleteTargetTypes.Report} 
                                createRoute={(_params) => `/reports`}
                            />
                        }
                    />
                </Route>
                <Route
                    path="reports/templates"
                    element={
                        <ReportTemplates />
                    }
                >
                    <Route
                        path="delete/:templateId"
                        element={
                            <DeleteDialogRoute 
                                targetType={DeleteTargetTypes.ReportTemplate} 
                                createRoute={(_params) => `/reports/templates`}
                            />
                        }
                    />
                </Route>
                <Route 
                    path="reports/create"
                    element={
                        <ReportDetails />
                    }
                />
                <Route
                    path="reports/edit/:reportId"
                    element={
                        <ReportDetails edit />
                    }
                />
                <Route 
                    path="reports/preview/:reportId"
                    element={
                        <ReportPreview />
                    }
                >
                    <Route
                        path="assets/edit/:assetId"
                        element={
                            <AssetFormRoute 
                                edit
                                createRoute={(params) => `/reports/preview/${params.reportId}`}
                            />
                        }
                    />
                    <Route
                        path="assets/create"
                        element={
                            <AssetFormRoute 
                                edit
                                createRoute={(params) => `/reports/preview/${params.reportId}`}
                            />
                        }
                    />
                    <Route
                        path="assets/delete/:assetId"
                        element={
                            <DeleteDialogRoute
                                createRoute={(params) => `/reports/preview/${params.reportId}`} 
                                targetType={DeleteTargetTypes.Asset}
                            />
                        }
                    />
                </Route>
                <Route
                    path="reports/templates/create"
                    element={
                        <ReportCustomTemplate />
                    }
                />
                <Route
                    path="reports/templates/edit/:templateId"
                    element={
                        <ReportCustomTemplate edit />
                    }
                />
                <Route
                    path="reports/templates/preview/:templateId"
                    element={
                        <ReportTemplatePreview />
                    }
                >
                    <Route
                        path="assets/edit/:assetId"
                        element={
                            <AssetFormRoute 
                                edit
                                createRoute={(params) => `/reports/templates/preview/${params.templateId}`}
                            />
                        }
                    />
                    <Route
                        path="assets/create/"
                        element={
                            <AssetFormRoute 
                                edit
                                createRoute={(params) => `/reports/templates/preview/${params.templateId}`}
                            />
                        }
                    />
                    <Route
                        path="assets/delete/:assetId"
                        element={
                            <DeleteDialogRoute
                                createRoute={(params) => `/reports/templates/preview/${params.templateId}`} 
                                targetType={DeleteTargetTypes.Asset}
                            />
                        }
                    />
                </Route>
                {/* <Route
                    path="reports/email-only/:reportName"
                    element={
                        <EmailOnlyReportDetails />
                    }
                /> */}
                {
                    permissions?.includes(Permissions.ReportsCanViewFinances) &&
                    <Route
                        path="finances"
                        element={
                            <Finances />
                        }
                    />
                }
                {
                    permissions?.includes(Permissions.AdminAccess) 
                    ? (
                        <Route
                            path="admin"
                        >
                            <Route 
                                index
                                element={
                                    <Administration />
                                }
                            />
                            <Route
                                path="agents"
                                element={
                                    <Agents />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <AgentFormRoute 
                                            createRoute={() => "/admin/agents"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:agentId"
                                    element={
                                        <AgentFormRoute 
                                            edit
                                            createRoute={() => "/admin/agents"}
                                        />
                                    }
                                />
                            </Route>

                            <Route
                                path="agent-locations"
                                element={
                                    <AgentLocations />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <AgentLocationFormRoute 
                                            createRoute={() => "/admin/agent-locations"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:agentLocationId"
                                    element={
                                        <AgentLocationFormRoute 
                                            edit
                                            createRoute={() => "/admin/agent-locations"}
                                        />
                                    }
                                />
                            </Route>
                            <Route
                                path="agent-personnel"
                                element={
                                    <AgentPersonnel />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <AgentPersonnelFormRoute 
                                            createRoute={() => "/admin/agent-personnel"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:userAgentLocationId"
                                    element={
                                        <AgentPersonnelFormRoute 
                                            edit
                                            createRoute={() => "/admin/agent-personnel"}
                                        />
                                    }
                                />
                            </Route>
                            <Route
                                path="agent-projects"
                                element={
                                    <AgentProjects />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <AgentProjectFormRoute 
                                            createRoute={() => "/admin/agent-locations"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:projectAgentLocationId"
                                    element={
                                        <AgentProjectFormRoute 
                                            edit
                                            createRoute={() => "/admin/agent-locations"}
                                        />
                                    }
                                />
                            </Route>
                            <Route 
                                path="tenants"
                                element= {
                                    <Tenants />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <TenantFormRoute 
                                            createRoute={() => "/admin/tenants"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:tenantId"
                                    element={
                                        <TenantFormRoute 
                                            edit
                                            createRoute={() => "/admin/tenants"}
                                        />
                                    }
                                />
                            </Route>
                            <Route 
                                path="lot-properties"
                                element= {
                                    <LotProperties />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <PropertyFormRoute 
                                            createRoute={() => "/admin/lot-properties"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:propertyId"
                                    element={
                                        <PropertyFormRoute 
                                            edit
                                            createRoute={() => "/admin/lot-properties"}
                                        />
                                    }
                                />
                            </Route>
                            <Route
                                path="attachment-types"
                                element={
                                    <AttachmentTypes />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <AttachmentTypeFormRoute 
                                            createRoute={() => "/admin/attachment-types"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:attachmentTypeId"
                                    element={
                                        <AttachmentTypeFormRoute 
                                            edit
                                            createRoute={() => "/admin/attachment-types"}
                                        />
                                    }
                                />
                            </Route>
                            <Route
                                path="stage-precincts"
                                element={
                                    <StagePrecincts />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <PrecinctFormRoute 
                                            createRoute={() => "/admin/stage-precincts"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:precinctId"
                                    element={
                                        <PrecinctFormRoute 
                                            edit
                                            createRoute={() => "/admin/stage-precincts"}
                                        />
                                    }
                                />
                            </Route>
                            <Route
                                path="project-permissions"
                                element={
                                    <ProjectPermissions />
                                }
                            >
                                <Route 
                                    path="create"
                                    element={
                                        <UserProjectFormRoute 
                                            createRoute={() => "/admin/project-permissions"}
                                        />
                                    }
                                />
                                <Route 
                                    path="edit/:userProjectId"
                                    element={
                                        <UserProjectFormRoute 
                                            edit
                                            createRoute={() => "/admin/project-permissions"}
                                        />
                                    }
                                />
                            </Route>
                        </Route>
                    ) : null
                }
            </Route>
            <Route 
                path="*" 
                element={<Navigate to="/projects" replace />}
            />
        </SentryRoutes>
    );
}

const AppProviders: FC<PropsWithChildren> = ({ children }) => {
    return (
        <ProjectsProvider>
            <ProjectProvider>
                <StagesProvider>
                    <StageProvider>
                        <LotsProvider>
                            <LotProvider>
                                <LinkProvider>
                                    {children}
                                </LinkProvider>
                            </LotProvider>
                        </LotsProvider>
                    </StageProvider>
                </StagesProvider>
            </ProjectProvider>
        </ProjectsProvider>
    )
}

const App: FC = () => {
    useSentryUser();

    return (
        <PermissionsProvider>
            <RoutingModel />
        </PermissionsProvider>
    );
}

export default App;